import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const {
    t
  } = useTranslation('shops');
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-11550" data-mp="s7zade3243f">
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-11551" data-mp="22igk78i3j2i0">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-11552" data-mp="1xkne0fgsm8hq">
          <div className="flex justify-between items-center mb-4" data-id="element-11553" data-mp="2c91ieutuy50e">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-11554" data-mp="2cacm6unazcri">
              {t('components.ShopDashboard.revenue_trend')}
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-11555" data-mp="2l96ngybkwrbe">
              <CalendarIcon size={14} className="mr-1" data-id="element-11556" data-mp="2lbkgl1mp7tgb" />
              {t('components.ShopDashboard.last_12_months')}
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-11557" data-mp="2v6ukxw0kkcx9">
            {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-11558" data-mp="2q8asrdvdu4wg">
                {hoveredRevenue.month}
                {t('components.ShopDashboard.usd')}{' '}
                {hoveredRevenue.revenue.toLocaleString('en-US')}
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-11559" data-mp="37iwvy498z3u6">
              <div data-id="element-11560" data-mp="3cj2ix74ko25x">
                {t('components.ShopDashboard.usd_2')}{' '}
                {maxRevenue.toLocaleString('en-US')}
              </div>
              <div data-id="element-11561" data-mp="32qdakl4bj4et">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.75).toLocaleString('en-US')}
              </div>
              <div data-id="element-11562" data-mp="2tqgn2mto0blc">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.5).toLocaleString('en-US')}
              </div>
              <div data-id="element-11563" data-mp="2evk7tpnfunyr">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.25).toLocaleString('en-US')}
              </div>
              <div data-id="element-11564" data-mp="d2mcnn66vjan">{t('components.ShopDashboard.usd_0')}</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-11565" data-mp="i04njj3ro8la">
              <div className="absolute inset-0" data-id="element-11566" data-mp="gkjsx0wm4emr">
                <div className="border-t border-gray-100 h-1/4" data-id="element-11567" data-mp="glbq2g5uykf5"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11568" data-mp="bjw32ik4e3u4"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11569" data-mp="6lktm8xqlvcf"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11570" data-mp="6nxasjvo81of"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-11571" data-mp="2m27xay3dg8j">
                {revenueData.map((item, index) => {
                const barHeight = `${item.revenue / maxRevenue * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-11572" data-mp="pkoz57q2l6zc" data-mpi={index}>
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-[#E2231A]' : 'bg-[#E2231A]/70 hover:bg-[#E2231A]'} rounded-t transition-all duration-200`} data-id="element-11573" data-mp="1daawdpmvtc4p"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-11574" data-mp="39xbuiq9y7vd5">
              {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-11575" data-mp="3etsqg6ne3e0s" data-mpi={index}>
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>

        {/* Orders Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-11576" data-mp="3oo2l5uzt0oeo">
          <div className="flex justify-between items-center mb-4" data-id="element-11577" data-mp="3smie0fikgr00">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-11578" data-mp="1i1urotj728j">
              {t('components.ShopDashboard.orders_trend')}
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-11579" data-mp="6dzem5406qq2">
              <CalendarIcon size={14} className="mr-1" data-id="element-11580" data-mp="3dh13qucgl11p" />
              {t('components.ShopDashboard.last_12_months')}
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-11581" data-mp="3miqqvnzb5dto">
            {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-11582" data-mp="3mgo3zctgc0t6">
                {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                {t('components.ShopDashboard.orders')}
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-11583" data-mp="33jwdaibkyjf3">
              <div data-id="element-11584" data-mp="2yt65kyfom4h8">{maxOrders.toLocaleString()}</div>
              <div data-id="element-11585" data-mp="2yrblfmc8ige7">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
              <div data-id="element-11586" data-mp="2b2aj8gtzw1hd">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
              <div data-id="element-11587" data-mp="2731r1gelsbnx">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
              <div data-id="element-11588" data-mp="1wyblz1nwkrfh">0</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-11589" data-mp="1wxa5gho4bcqd">
              <div className="absolute inset-0" data-id="element-11590" data-mp="21pjqpkeafomb">
                <div className="border-t border-gray-100 h-1/4" data-id="element-11591" data-mp="21qcviftos41t"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11592" data-mp="1n1gmogjt2n05"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11593" data-mp="1ryp6elklqz3i"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-11594" data-mp="1rwvxezmio2sc"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-11595" data-mp="2fmq93dv3nlb0">
                {ordersData.map((item, index) => {
                const barHeight = `${item.orders / maxOrders * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-11596" data-mp="2apgbvc1gy5cm" data-mpi={index}>
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-400 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-11597" data-mp="2xotuf5qfofmj"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-11598" data-mp="2exj7ezntwmzx">
              {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-11599" data-mp="2jv2mg1scp3sx" data-mpi={index}>
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-11600" data-mp="38rm2azxtqbk">
        {/* Top Products */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-11601" data-mp="87mdu2fvup7t">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-11602" data-mp="85iabllwbs82">
            {t('components.ShopDashboard.top_selling_products')}
          </h4>
          <div className="space-y-3" data-id="element-11603" data-mp="1nrbzi3dig9or">
            {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-11604" data-mp="22fq9cy9cgyet" data-mpi={index}>
                <div className="flex items-center" data-id="element-11605" data-mp="2bfv3w7tfw2bk">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-semibold text-gray-500 mr-3" data-id="element-11606" data-mp="2bek22djk14n3">
                    {index + 1}
                  </div>
                  <div data-id="element-11607" data-mp="2ldstayn1c55r">
                    <p className="text-sm font-medium text-gray-900" data-id="element-11608" data-mp="2lbyhqaxnlpec">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-11609" data-mp="qzrtaafr1c43">
                      {product.sales} {t('components.ShopDashboard.sold')}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-11610" data-mp="2m3zkzfd31jcm">
                  <p className="text-sm font-medium text-gray-900" data-id="element-11611" data-mp="2m5apjhecqv4s">
                    {t('components.ShopDashboard.usd_2')}{' '}
                    {product.revenue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-11612" data-mp="352bzzhqodng4">
                    {t('components.ShopDashboard.revenue')}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Customer Insights */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-11613" data-mp="3esnbpqglp0xk">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-11614" data-mp="3isnt9jyzxs8u">
            {t('components.ShopDashboard.customer_insights')}
          </h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-11615" data-mp="3nqz1jq9n7uoy">
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-11616" data-mp="3ssy26se00di7">
              <p className="text-xs text-gray-500 mb-1" data-id="element-11617" data-mp="6qfot0mi66yv">
                {t('components.ShopDashboard.new_customers')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-11618" data-mp="1qb40yekaq2u">
                {customerStats.new}
              </p>
              <div className="mt-1 flex items-center" data-id="element-11619" data-mp="3sy5t57bgz66i">
                <span className="text-xs text-green-600 flex items-center" data-id="element-11620" data-mp="3owbybfnygwe9">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-11621" data-mp="3oyew4lvcnttj" />
                  14.3%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-11622" data-mp="khkilm36w51a">
              <p className="text-xs text-gray-500 mb-1" data-id="element-11623" data-mp="kk6olhafmche">
                {t('components.ShopDashboard.returning_customers')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-11624" data-mp="boh6pi87008r">
                {customerStats.returning}
              </p>
              <div className="mt-1 flex items-center" data-id="element-11625" data-mp="188zkxeuyumcj">
                <span className="text-xs text-green-600 flex items-center" data-id="element-11626" data-mp="13g6i4wj25o33">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-11627" data-mp="13evh34gzyufo" />
                  7.2%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-11628" data-mp="syt6qbfaffci">
              <p className="text-xs text-gray-500 mb-1" data-id="element-11629" data-mp="svf73ay0dk8r">
                {t('components.ShopDashboard.avg_order_value')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-11630" data-mp="iyu1ane6053v">
                {t('components.ShopDashboard.usd_2')}{' '}
                {(shop.revenue.total / shop.ordersCount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
              <div className="mt-1 flex items-center" data-id="element-11631" data-mp="11xodppq5otgv">
                <span className="text-xs text-green-600 flex items-center" data-id="element-11632" data-mp="11wdd1ys0c1tz">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-11633" data-mp="1pmhfi2tglvje" />
                  3.5%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-11634" data-mp="3f562f19iw9db">
              <p className="text-xs text-gray-500 mb-1" data-id="element-11635" data-mp="3f3lbdlmf38k7">
                {t('components.ShopDashboard.conversion_rate')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-11636" data-mp="3ouzee3dkuqi3">
                {customerStats.conversionRate}%
              </p>
              <div className="mt-1 flex items-center" data-id="element-11637" data-mp="3jvmjqwggsf4y">
                <span className="text-xs text-green-600 flex items-center" data-id="element-11638" data-mp="3jy8q4srmcoll">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-11639" data-mp="2w6k3nt1hauck" />
                  1.2%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}